import { ThinkingEffort } from '../chats/thinkingEffort';
import { ChatFile } from '../chats/types';
import { VerifyEvidence, VerifyOutcome } from './verify/types';

export const ChatMode = {
  CLIENT: 'CLIENT',
  DOCUMENT: 'DOCUMENT',
  FAMILY: 'FAMILY',
} as const;

export type ChatMode = (typeof ChatMode)[keyof typeof ChatMode];

export const ChatState = {
  THINKING: 'THINKING',
  ANSWERING: 'ANSWERING',
  EDITING: 'EDITING',
  FINAL: 'FINAL',
  CANCELLED: 'CANCELLED',
  TOOL_STARTED: 'TOOL_STARTED',
  TOOL_FINISHED: 'TOOL_FINISHED',
  TOOL_LIMIT_REACHED: 'TOOL_LIMIT_REACHED',
  FILE_PRESENTED: 'FILE_PRESENTED',
  DATA_UPDATED: 'DATA_UPDATED',
  ACTION_PROPOSED: 'ACTION_PROPOSED',
  FOLLOW_UPS_SUGGESTED: 'FOLLOW_UPS_SUGGESTED',
  EMAIL_DRAFTED: 'EMAIL_DRAFTED',
  OUTLOOK_CONNECT_REQUIRED: 'OUTLOOK_CONNECT_REQUIRED',
  RETRY_NOTICE: 'RETRY_NOTICE',
  RESTARTED: 'RESTARTED',
} as const;

export type ChatState = (typeof ChatState)[keyof typeof ChatState];

export type FinalChunkData = {
  conversationId: string;
  editedContent: string;
  editedContentWithMarkers?: string | null;
  assistantMessage: string;
  versionId: string | null;
  versionNumber: number | null;
  versioningFailed: boolean;
  evidence?: VerifyEvidence[];
  verifyOutcome?: VerifyOutcome;
  verifyHeadline?: string;
  verifyUnresolved?: boolean;
};

export enum MessageDirection {
  Incoming = 'incoming',
  Outgoing = 'outgoing',
}

export type MessageState = 'SENT' | 'ANSWERING' | 'THINKING' | string;

export type MessageContent = { state: MessageState; content: string };

export type PresentedFile = { description: string; url: string };

export type ScenarioUpdate = {
  domain: string;
  clientId: string;
  scenarioId: string;
  // Set for family-scoped models; their detail page lives on the family route.
  familyId?: string;
  phase: 'committed' | 'ran' | 'reverted' | string;
  headline: string;
};

export type ProposedActionStatus = 'PENDING' | 'APPLIED' | 'DENIED' | 'FAILED' | 'SUPERSEDED';

// `kind` is optional on template proposals for deploy overlap: chunks emitted by an older
// backend carry no discriminator and must keep rendering as template widgets.
export type TemplateProposedAction = {
  kind?: 'TEMPLATE';
  proposalId: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  templateId?: string | null;
  templateName: string;
  templateType: 'MEETING_NOTE' | 'MEETING_AGENDA';
  summary: string;
  changeStats?: {
    sectionsAdded: number;
    sectionsRemoved: number;
    sectionsModified: number;
  } | null;
  status?: ProposedActionStatus;
  failureReason?: string | null;
};

type ClientDataFieldChange = {
  field: string;
  label: string;
  before?: unknown;
  after?: unknown;
};

export type ClientDataOperationView = {
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  entityType: string;
  entityTypeLabel: string;
  entityLabel: string;
  clientId: string;
  clientName: string;
  fieldChanges: ClientDataFieldChange[];
};

export type ClientDataProposedAction = {
  kind: 'CLIENT_DATA';
  proposalId: string;
  summary: string;
  focusedClientId: string | null;
  operations: ClientDataOperationView[];
  status?: ProposedActionStatus;
  failureReason?: string | null;
};

export type ProposalDiffLine = {
  type: 'added' | 'removed' | 'context';
  text: string;
};

export type MemoryProposalItemView = {
  scope: 'ADVISOR' | 'CLIENT' | 'COMPANY';
  clientName?: string | null;
  addedCount: number;
  removedCount: number;
  diffLines: ProposalDiffLine[];
  diffTruncated?: boolean;
  isClear?: boolean;
};

export type MemoryProposedAction = {
  kind: 'MEMORY';
  proposalId: string;
  reason: string;
  items: MemoryProposalItemView[];
  status?: ProposedActionStatus;
  failureReason?: string | null;
};

export type SkillProposedAction = {
  kind: 'SKILL';
  proposalId: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE' | 'SET_ENABLED';
  skillName: string;
  newName?: string | null;
  summary: string;
  addedCount: number;
  removedCount: number;
  diffLines: ProposalDiffLine[];
  diffTruncated?: boolean;
  status?: ProposedActionStatus;
  failureReason?: string | null;
};

export type MeetingActionOperationView = {
  operation: 'CREATE' | 'UPDATE' | 'COMPLETE' | 'REOPEN' | 'REMOVE';
  eventId?: string | null;
  actionId?: string | null;
  description?: string | null;
  owner?: string | null;
  dueDate?: string | null;
  baseDescription?: string | null;
};

export type MeetingActionProposedAction = {
  kind: 'MEETING_ACTIONS';
  proposalId: string;
  summary: string;
  meetingName?: string | null;
  operations: MeetingActionOperationView[];
  status?: ProposedActionStatus;
  failureReason?: string | null;
};

export type ProposedAction =
  | TemplateProposedAction
  | ClientDataProposedAction
  | MemoryProposedAction
  | SkillProposedAction
  | MeetingActionProposedAction;

export const isClientDataProposal = (
  proposal: ProposedAction,
): proposal is ClientDataProposedAction => proposal.kind === 'CLIENT_DATA';

export const isMemoryProposal = (proposal: ProposedAction): proposal is MemoryProposedAction =>
  proposal.kind === 'MEMORY';

export const isSkillProposal = (proposal: ProposedAction): proposal is SkillProposedAction =>
  proposal.kind === 'SKILL';
export const isMeetingActionProposal = (
  proposal: ProposedAction,
): proposal is MeetingActionProposedAction => proposal.kind === 'MEETING_ACTIONS';

export type EmailDraftAttachment = {
  fileId: number;
  fileName: string;
};

export type EmailDraft = {
  draftId: string;
  // Empty when no recipient could be resolved; the user fills it in from their email client.
  to: string;
  subject: string;
  body: string;
  // Siesta's attachment proposal; the user can swap or remove it on the card before sending.
  attachment?: EmailDraftAttachment | null;
  sentAt?: string | null;
  savedToOutlookAt?: string | null;
};
export type OutlookConnectRequest = {
  requestId: string;
};

export type MessageAttachment = { filename: string; extension: string };
export type MessageSkill = { name: string; label?: string | null };

export type ChatMessage = {
  kind: 'message';
  id: string;
  response: MessageContent[];
  sender: string;
  direction: MessageDirection;
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
  currentToolName?: string;
  retryNotice?: string;
  presentedFiles?: PresentedFile[];
  scenarioUpdates?: ScenarioUpdate[];
  proposedActions?: ProposedAction[];
  // One-tap next steps Siesta offered under this reply.
  followUps?: string[];
  emailDrafts?: EmailDraft[];
  outlookConnectRequests?: OutlookConnectRequest[];
  attachments?: MessageAttachment[];
  skill?: MessageSkill;
  // Response length at the last completed tool call; RESTARTED rewinds to here.
  restartCheckpointIndex?: number;
  verifyOutcome?: VerifyOutcome;
  evidence?: VerifyEvidence[];
  verifyMeetingNoteId?: string;
  verifyHeadline?: string;
  verifyUnresolved?: boolean;
  // The model's short spoken answer, shown above the evidence in place of canned outcome copy.
  verifyMessage?: string;
};

type InterruptedMarker = {
  kind: 'interrupted';
  id: string;
};

// A turn whose background worker never returned: the stream ended without an answer, so we mark
// the gap rather than leaving the question looking unanswered or spinning forever.
type FailedMarker = {
  kind: 'failed';
  id: string;
};

export type RetryPayload = {
  content: string;
  clientId: string | null;
  conversationId: string;
  attachedFiles: ChatFile[];
  verify?: { sectionPath?: string[] };
  silent: boolean;
  skill?: MessageSkill;
  thinkingEffort?: ThinkingEffort;
  // Cleared once the turn is known to have produced nothing, which is what makes a resend safe
  // rather than a rejoin.
  turnId?: string;
};

// A turn that errored mid-stream. Unlike FailedMarker this is a hard failure the user can retry,
// so it renders as an error bubble with a Retry action rather than a quiet divider.
export type ErrorMarker = {
  kind: 'error';
  id: string;
  retryPayload?: RetryPayload;
};

export type Message = ChatMessage | InterruptedMarker | FailedMarker | ErrorMarker;

export const isChatMessage = (m: Message): m is ChatMessage => m.kind === 'message';

export type StoredMessage = {
  role: string;
  content?: string | null;
  attachments?: MessageAttachment[];
  skill?: MessageSkill;
};
